// src/live/components/LiveCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleDot, Users, Video as VideoIcon } from 'lucide-react';
import { LiveIndexEntry } from '../types/liveIndex';
import { getLiveNavigationTarget } from '../../lib/services/liveNavigation';
import { resolveLiveRole } from '../types/liveRole';
import { useCurrentUser } from '../../contexts/UserContext';
import { getLiveSourceBadge } from '../utils/liveSourceBadge';

interface LiveCardProps {
  live: LiveIndexEntry;
  compact?: boolean;
}

export default function LiveCard({ live, compact = false }: LiveCardProps) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.uid;

  const role = resolveLiveRole({
    hostId: live.hostId,
    allowedUsers: live.allowedUsers,
    userId,
  });

  const target = getLiveNavigationTarget(live, role);
  const isParticipant = role === 'host' || role === 'invited_participant';
  const viewerCount = live.viewerCount ?? live.viewers;
  const isEvent = live.source === 'event';

  const handleClick = () => navigate(target);

  const sourceBadge = getLiveSourceBadge(live.source);

  if (compact) {
    return (
      <div
        onClick={handleClick}
        className="w-64 h-36 rounded-lg shadow-lg relative flex-shrink-0 cursor-pointer overflow-hidden group"
        style={{ backgroundColor: 'var(--vb-bg-secondary)' }}
      >
        {live.thumbnailUrl ? (
          <img src={live.thumbnailUrl} alt={live.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-orange-600" />
        )}
        <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition" />
        <div className="absolute top-2 left-2 flex items-center gap-2">
          <span className="bg-red-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1 animate-pulse">
            <CircleDot className="w-3 h-3" />
            {live.status === 'upcoming' ? 'UPCOMING' : 'LIVE'}
          </span>
          {live.source !== 'native' && (
            <span className={`${sourceBadge.className} text-white px-2 py-1 rounded text-xs`}>
              {sourceBadge.label}
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
          <Users className="w-3 h-3" />
          {viewerCount}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="text-white text-sm font-medium line-clamp-2">{live.title}</p>
          <p className="text-white/70 text-xs">{live.hostName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
      <div className="relative aspect-video">
        {live.thumbnailUrl ? (
          <img src={live.thumbnailUrl} alt={live.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
            {live.source === 'meeting' || live.source === 'event' ? (
              <Users className="w-12 h-12 text-white" />
            ) : (
              <VideoIcon className="w-12 h-12 text-white" />
            )}
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-2">
          <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <CircleDot className="w-3 h-3" />
            {live.status === 'upcoming' ? 'UPCOMING' : 'LIVE'}
          </span>
        </div>
        <div className={`absolute top-2 right-2 ${sourceBadge.className} text-white px-2 py-1 rounded-full text-xs`}>
          {sourceBadge.label}
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-white font-semibold mb-2 line-clamp-2">{live.title}</h3>
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-3">
          {live.hostAvatar ? (
            <img src={live.hostAvatar} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-xs">
              {live.hostName.charAt(0).toUpperCase()}
            </div>
          )}
          <span>{live.hostName}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-zinc-400 flex-wrap">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {viewerCount.toLocaleString()} {isEvent ? 'watching now' : 'watching'}
          </div>
          {isEvent && typeof live.stageCount === 'number' && (
            <div>{live.stageCount.toLocaleString()} on stage</div>
          )}
          {isEvent && typeof live.rsvpCount === 'number' && (
            <div>{live.rsvpCount.toLocaleString()} RSVP'd</div>
          )}
        </div>
      </div>

      <div className="px-4 pb-4">
        <button
          onClick={handleClick}
          className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-4 py-2 rounded-lg flex items-center justify-center gap-2"
        >
          {isParticipant && (live.source === 'meeting' || live.source === 'event') ? (
            <>
              <Users className="w-4 h-4" />
              Join Room
            </>
          ) : (
            <>
              <VideoIcon className="w-4 h-4" />
              Watch Live
            </>
          )}
        </button>
      </div>
    </div>
  );
}
